"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface SelectedProduct {
  id: number;
  title: string;
  featured_image_url: string;
  price: number;
  short_description: string;
  long_description: string;
  link: string;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  const [selectedProduct, setSelectedProduct] = React.useState<SelectedProduct | null>(null);
  if (loading) {
    return <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[4/5] bg-muted animate-pulse" />
            <div className="space-y-2 px-1">
              <div className="h-3 w-1/3 bg-muted animate-pulse" />
              <div className="h-4 w-2/3 bg-muted animate-pulse" />
              <div className="h-4 w-1/4 bg-muted animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-neutral-gray">
        Unable to load the gallery.{" "}
        <Button variant="link" onClick={refetch} className="h-auto p-0 text-sm underline">
          Try again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-sm text-neutral-gray font-mono uppercase tracking-widest">
        The archive is currently being curated.
      </p>;
  }
  return <>
      <div className="grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 md:gap-x-8 md:gap-y-16 lg:grid-cols-4">
        {products.slice(0, 8).map((product, i) => {
        const origin = product.categories && product.categories.length > 0 ? product.categories[0].name : "";
        const material = product.tags && product.tags.length > 0 ? product.tags[0].name : "";
        const eyebrowParts = [material, origin].filter(Boolean);
        const eyebrow = eyebrowParts.join(" \u00B7 ");
        return <article key={product.id} data-index={i} className="group flex flex-col">
              <div className="relative bg-muted p-0 overflow-hidden">
                <Link to={product.link} className="block">
                  <div className="aspect-[4/5] overflow-hidden">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <div className="h-full w-full bg-card" />}
                  </div>
                </Link>

                <div className="pointer-events-none absolute bottom-4 right-4 translate-y-3 opacity-0 transition-all duration-300 ease-out group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
                  <Button variant="outline" size="sm" onClick={() => setSelectedProduct({
                id: product.id,
                title: product.title,
                featured_image_url: product.featured_image_url,
                price: product.price,
                short_description: product.short_description,
                long_description: product.long_description,
                link: product.link
              })} className="h-8 border-stark-white bg-base-black px-4 text-[10px] font-mono uppercase tracking-[0.2em] text-stark-white backdrop-blur-sm hover:bg-stark-white hover:text-base-black">
                    View
                  </Button>
                </div>
              </div>

              <div className="mt-5 px-1">
                {eyebrow && <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-gray">
                    {eyebrow}
                  </p>}
                <Link to={product.link} className="mt-2 block font-default text-lg font-normal text-foreground hover:underline underline-offset-4">
                  {product.title}
                </Link>
                <p className="mt-2 font-mono text-sm tabular-nums text-foreground">
                  $ {product.price}
                </p>
              </div>
            </article>;
      })}
      </div>

      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-4xl border-border bg-background p-0 sm:max-w-4xl">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="bg-muted p-0">
                <div className="aspect-[4/5] overflow-hidden">
                  {selectedProduct.featured_image_url ? <img src={selectedProduct.featured_image_url} alt={selectedProduct.title} loading="lazy" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-card" />}
                </div>
              </div>

              <div className="flex flex-col p-8 md:p-10">
                <DialogHeader className="space-y-3 text-left">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-gray">
                    John John Fine Photography
                  </p>
                  <DialogTitle className="font-default text-2xl md:text-3xl font-normal leading-tight text-foreground">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <p className="mt-4 font-mono text-base tabular-nums text-foreground">
                  $ {selectedProduct.price}
                </p>

                {selectedProduct.short_description && <div className="mt-6 font-default text-sm leading-relaxed text-neutral-gray [&_p]:mb-2 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-8 border-t border-border pt-6">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-gray">
                    Provenance
                  </p>
                  <p className="mt-2 font-default text-sm text-foreground">
                    Limited edition film print. Hand-signed and numbered.
                  </p>
                </div>

                <div className="mt-8">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="bg-foreground text-background rounded-none" text="Acquire Print" showQuantity={true} />
                </div>

                <Link to={selectedProduct.link} className="mt-4 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-gray hover:text-foreground">
                  View Full Details
                </Link>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function Collection() {
  const wp_query_params = {
    per_page: 8
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4993" id="the-collection" className="bg-background text-foreground py-24 md:py-32 lg:py-44">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-end md:gap-12">
          <div className="md:col-span-7">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-gray">
              &mdash; 03 / The Archive
            </p>
            <h2 className="mt-6 font-default text-5xl font-normal leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-7xl">
              Limited production,{" "}
              <span className="italic font-default">ultra high end.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-default text-base leading-relaxed text-neutral-gray md:max-w-sm md:ml-auto">
              A deliberate selection of fine film photography, curated for the discerning collector.
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          <EcommerceProductsProvider wp_query={{
          per_page: 8
        }}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>
        </div>

        <div className="mt-20 flex justify-center md:mt-24">
          <Link to="/ShopPage" className="group inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-foreground">
            <span className="relative">
              Browse the Full Archive
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-foreground transition-transform duration-500 ease-out group-hover:scale-x-0" />
              <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-foreground transition-transform duration-500 ease-out group-hover:scale-x-100" />
            </span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}